import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import dayjs from "dayjs";
import { getAllIncapacities, exportIncapacitiesExcel } from "../../services/incapacities.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Button from "../../components/Button.tsx";
import FilterIncapacities from "../../components/incapacities/FilterIncapacities.tsx";
import PaginationIncapacities from "../../components/incapacities/PaginationIncapacities.tsx";
import ImportIncapacitiesExcel from "../../components/incapacities/ImportIncapacitiesExcel.tsx";
import { fetchDirectBossesByBossId } from "../../services/direct-boss.service.ts";
import {
  setIncapacities,
  setTotal,
  setPage,
  setSortBy,
  setDefaultSort,
} from "../../store/slices/incapacitiesSlice.ts";
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import moment from "moment";
import "moment/locale/es";
import XLSX from "xlsx-js-style";

moment.locale("es");

interface Incapacity {
  id: number;
  approved: boolean;
  isRejected: boolean;
  isRequest: boolean;
  startDate: number;
  endDate: number;
  daysOff: number;
  title: string;
  description: string;
  createdAt: string;
  partner?: {
    person?: {
      firstName?: string;
      lastName?: string;
      secondLastName?: string;
    };
  };
  reviewerBy?: {
    name?: string;
    lastName?: string;
  };
}

interface IncapacityParams {
  limit: number;
  offset: number;
  param: string;
  sort: string;
  order: string;
  status: string;
  incapacityDate: string;
  employeeIds?: number[];
}

interface TableColumn {
  key: string;
  label: string;
  sortable: boolean;
  render: (incapacity: Incapacity) => any;
}

const SORTABLE_COLUMNS = {
  ID: "id",
  PARTNER: "partner",
  START_DATE: "startDate",
  END_DATE: "endDate",
  DAYS_OFF: "daysOff",
  STATUS: "status",
  REVIEWER: "reviewer",
} as const;

const formatDate = (timestamp: number): string => {
  if (!timestamp) return "-";
  return moment.unix(timestamp).format("ddd DD MMM YYYY");
};

const calculateDaysOff = (startDate: number, endDate: number): number => {
  if (!startDate || !endDate) return 0;
  const start = moment.unix(startDate);
  const end = moment.unix(endDate);
  return end.diff(start, 'days') + 1; // +1 para incluir el día inicial
};

const StatusBadge = ({ incapacity }: { incapacity: Incapacity }) => {
  const { approved, isRejected } = incapacity;

  // Colores suaves tipo pastel
  let bg = "#fef9c3"; // pending
  let color = "#854d0e";
  let label = "Pendiente";

  if (approved) {
    bg = "#bbf7d0";
    color = "#166534";
    label = "Aprobado";
  } else if (isRejected) {
    bg = "#fee2e2";
    color = "#b91c1c";
    label = "Rechazado";
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.20rem 0.7rem",
        borderRadius: "999px",
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.03em",
        backgroundColor: bg,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const TableHeader = ({
  column,
  label,
  sortBy,
  defaultSort,
  onSort,
  isSortable = false,
}: {
  column: string;
  label: string;
  sortBy: string;
  defaultSort: string;
  onSort: (column: string) => void;
  isSortable?: boolean;
}) => {
  const getIconSort = () => (
    <i
      className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
    />
  );

  if (!isSortable) {
    return <th className="text-center">{label}</th>;
  }

  return (
    <th
      style={{ ...fixedStyle } as any}
      className="text-center cursor-pointer"
      onClick={() => onSort(column)}
    >
      {label} {sortBy === column && getIconSort()}
    </th>
  );
};

const actionButtonStyle: React.CSSProperties = {
  borderRadius: "999px",
  width: 32,
  height: 32,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const ActionButtons = ({
  incapacity,
  onReview,
}: {
  incapacity: Incapacity;
  onReview: (incapacity: Incapacity) => void;
}) => (
  <div className="d-flex justify-content-center align-items-center">
    <button
      type="button"
      className="btn btn-azul btn-sm"
      style={actionButtonStyle}
      title="Revisar incapacidad"
      onClick={() => onReview(incapacity)}
    >
      <i className="fa-solid fa-eye" />
    </button>
  </div>
);

const AllIncapacities = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [employeeIds, setEmployeeIds] = useState<number[]>([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  useAppSettings();

  const { isDirectBoss } = useSelector((state: RootState) => state.directBoss);
  const { bossId } = useParams();
  const { incapacities, total, page, limit, query, sortBy, defaultSort } =
    useSelector((state: RootState) => state.incapacities);

  useEffect(() => {
    const fetchDirectBossEmployees = async () => {
      const effectiveBossId = bossId ? Number(bossId) : null;
      if (effectiveBossId) {
        try {
          const directBosses = await fetchDirectBossesByBossId(effectiveBossId);
          const allEmployeeIds: number[] = [];
          
          directBosses.forEach(directBoss => {
            directBoss.employees.forEach(employee => {
              allEmployeeIds.push(employee.id);
            });
          });
          
          setEmployeeIds(allEmployeeIds);
        } catch (error) {
          console.error("Error fetching direct boss employees:", error);
          setEmployeeIds([]);
        }
      }
    };

    fetchDirectBossEmployees();
  }, [bossId]);

  const incapacityParams = useMemo((): IncapacityParams => {
    const offset = page * limit;
    const param = query.includes("param=")
      ? query.split("param=")[1]?.split("&")[0] || ""
      : "";
    const status = query.includes("status=")
      ? query.split("status=")[1]?.split("&")[0] || "all"
      : "all";
    const incapacityDate = query.includes("incapacityDate=")
      ? query.split("incapacityDate=")[1]?.split("&")[0] || ""
      : "";

    const params: IncapacityParams = {
      limit,
      offset,
      param,
      sort: sortBy,
      order: defaultSort.toUpperCase(),
      status,
      incapacityDate,
    };

    // Si viene bossId (acceso desde menú de jefe directo), usar endpoint dedicado
    if (bossId) {
      // @ts-ignore
      params.bossId = Number(bossId);
    }

    return params;
  }, [page, limit, query, sortBy, defaultSort, bossId, employeeIds]);

  const handleSort = useCallback(
    (column: string): void => {
      const sortOrder =
        sortBy !== column ? "asc" : defaultSort === "asc" ? "desc" : "asc";

      dispatch(setSortBy(column));
      dispatch(setPage(0));
      dispatch(setDefaultSort(sortOrder));
    },
    [dispatch, sortBy, defaultSort]
  );

  const handleReview = useCallback(
    (incapacity: Incapacity): void => {
      navigate(`/colaboradores/aprobar-incapacidad/${incapacity.id}`);
    },
    [navigate]
  );

  const getTableColumns = useCallback((): TableColumn[] => [
    {
      key: SORTABLE_COLUMNS.ID,
      label: "Id",
      sortable: true,
      render: (incapacity) => `${incapacity.id}`,
    },
    {
      key: SORTABLE_COLUMNS.PARTNER,
      label: "Colaborador",
      sortable: true,
      render: (incapacity) =>
        incapacity.partner?.person
          ? toCamelCase(
              `${incapacity.partner.person.firstName || ""} ${
                incapacity.partner.person.lastName || ""
              } ${incapacity.partner.person.secondLastName || ""}`
            )
          : "-",
    },

    {
      key: SORTABLE_COLUMNS.START_DATE,
      label: "Rango de fechas",
      sortable: true,
      render: (incapacity) => {
        if (!incapacity.startDate || !incapacity.endDate) return "-";
        const start = moment.unix(incapacity.startDate);
        const end = moment.unix(incapacity.endDate);
        return (
          <div style={{ lineHeight: 1.35 }}>
            <div style={{ fontWeight: 500 }}>
              Del {start.format("ddd DD MMM YYYY")}
            </div>
            <div
              style={{
                fontSize: "0.85em",
                color: "var(--bs-secondary-color)",
              }}
            >
              Al {end.format("ddd DD MMM YYYY")}
            </div>
          </div>
        );
      },
    },
    {
      key: SORTABLE_COLUMNS.DAYS_OFF,
      label: "Días de incapacidad",
      sortable: true,
      render: (incapacity) => (
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
          }}
        >
          {calculateDaysOff(incapacity.startDate, incapacity.endDate)} días
        </span>
      ),
    },
    {
      key: SORTABLE_COLUMNS.STATUS,
      label: "Estado",
      sortable: true,
      render: (incapacity) => <StatusBadge incapacity={incapacity} />,
    },
    {
      key: SORTABLE_COLUMNS.REVIEWER,
      label: "Revisado por",
      sortable: true,
      render: (incapacity) =>
        incapacity.reviewerBy
          ? `${incapacity.reviewerBy.name || ""} ${
              incapacity.reviewerBy.lastName || ""
            }`.trim() || "-"
          : "-",
    },
    {
      key: "actions",
      label: "Acciones",
      sortable: false,
      render: (incapacity) => (
        <ActionButtons
          incapacity={incapacity}
          onReview={handleReview}
        />
      ),
    },
  ], [handleReview]);

  const tableColumns = useMemo(() => getTableColumns(), [getTableColumns]);

  const fetchIncapacities = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAllIncapacities(incapacityParams);
      dispatch(setIncapacities(response.data || []));
      dispatch(setTotal(response.total || 0));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error desconocido al cargar incapacidades";
      setError(errorMessage);
      console.error("Error fetching incapacities:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, incapacityParams]);

  useEffect(() => {
    fetchIncapacities();
  }, [fetchIncapacities]);

  const renderTableRow = useCallback(
    (incapacity: Incapacity) => (
      <tr key={incapacity.id}>
        {tableColumns.map((column) => (
          <td key={column.key} className="text-center">
            {column.render(incapacity)}
          </td>
        ))}
      </tr>
    ),
    [tableColumns]
  );

  const renderTableHeaders = useCallback(
    () => (
      <tr style={{ ...fixedHeadStyle } as any}>
        {tableColumns.map((column) => (
          <TableHeader
            column={column.key}
            label={column.label}
            sortBy={sortBy}
            defaultSort={defaultSort}
            onSort={handleSort}
            isSortable={column.sortable}
          />
        ))}
      </tr>
    ),
    [tableColumns, sortBy, defaultSort, handleSort]
  );

  const renderEmptyState = useCallback(
    () => (
      <tr>
        <td
          colSpan={tableColumns.length}
          className="text-center text-muted py-4"
        >
          <div
            className="py-4 justify-content-center align-items-center"
            style={{ height: "450px" }}
          >
            <h5 className="text-secondary text-center fs-20px">
              {error ? (
                <div>
                  <i className="fa fa-exclamation-triangle text-warning me-2"></i>
                  Error al cargar incapacidades: {error}
                </div>
              ) : bossId && employeeIds.length === 0 ? (
                "No tienes empleados asignados como jefe directo"
              ) : (
                "Aún no existen incapacidades registradas"
              )}
            </h5>
            {!error && !bossId && (
              <>
                <h5 className="text-center text-secondary fw-400 fs-15px">
                  Aqui podras revisar las incapacidades de los colaboradores
                </h5>
                <h5 className="text-center text-secondary fw-400">
                  y aprobarlas
                </h5>
              </>
            )}
            {!error && bossId && employeeIds.length === 0 && (
              <h5 className="text-center text-secondary fw-400 fs-15px">
                Contacta al administrador para asignarte empleados
              </h5>
            )}
          </div>
        </td>
      </tr>
    ),
    [error, tableColumns.length, isDirectBoss, employeeIds.length]
  );

  return (
    <Layout>
      <Title
        baseTitle="Incapacidades"
        basePath="/colaboradores/incapacidades"
        title={
          isDirectBoss
            ? "Incapacidades de mis empleados" 
            : "Listado de incapacidades"
        }
      />
      
      <div className="card border-0 m-4">
        <div className="tab-pane fade show active" id="sucursal">
          <div className="card-body">
            <Panel>
              <div className="input-group w-100">
                <div className="d-flex align-items-center w-100">
                  <FilterIncapacities />
                </div>
              </div>

              <div
                className="mb-3"
                style={{ borderRadius: "0.75rem", overflow: "hidden" }}
              >
                <div
                  className="table-responsive"
                  style={{ overflowY: "auto", height: "500px" }}
                >
                  <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                    <thead>{renderTableHeaders()}</thead>
                    <tbody>
                      {incapacities?.length > 0
                        ? incapacities.map(renderTableRow)
                        : renderEmptyState()}
                    </tbody>
                  </table>
                </div>
              </div>

              <PaginationIncapacities />
            </Panel>
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default AllIncapacities;
